import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import {
  User, Mail, CreditCard, AlertTriangle, Loader2, Eye, EyeOff, Lock, Activity, Gift, Copy, Check, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';

const PROFILE_BREADCRUMBS = [{ label: 'Профил', href: '/profile' }];
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GENDER_LABELS = { male: 'Мъж', female: 'Жена' };

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Physical profile & generation status
  const [profileInfo, setProfileInfo] = useState(null);
  const [genStatus, setGenStatus] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile`)
      .then(r => setProfileInfo(r.data))
      .catch(() => {});
    axios.get(`${API}/plans/generations-status`)
      .then(r => setGenStatus(r.data))
      .catch(() => {});
    axios.get(`${API}/referral/info`)
      .then(r => setReferralInfo(r.data))
      .catch(() => {});
  }, []);

  const handleCopyReferral = () => {
    if (!referralInfo?.referral_link) return;
    navigator.clipboard.writeText(referralInfo.referral_link)
      .then(() => {
        setReferralCopied(true);
        toast.success('Реферален линк е копиран!');
        setTimeout(() => setReferralCopied(false), 2500);
      })
      .catch(() => toast.error('Неуспешно копиране'));
  };

  const isActive = user?.subscription_status === 'active';

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() ?? '?';
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'ИЗТРИЙ') {
      toast.error('Въведи "ИЗТРИЙ" за потвърждение');
      return;
    }
    if (!deletePassword) {
      toast.error('Въведи паролата си');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/account`, { data: { password: deletePassword } });
      navigate('/');
      logout();
      toast.success('Акаунтът е изтрит успешно');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при изтриване на акаунта');
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setShowPassword(false);
  };

  return (
    <>
      <SEOMeta
        title="Профил"
        description="Управлявай профила си в portion.bg."
        canonical="/profile"
        noindex={true}
        breadcrumbs={PROFILE_BREADCRUMBS}
      />
      <PageBreadcrumb items={PROFILE_BREADCRUMBS} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="space-y-6"
        >
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Профил</h1>
            <p className="text-slate-500 dark:text-slate-400">Информация за акаунта и настройки</p>
          </div>

          {/* ── User Info Card ── */}
          <Card
            className="rounded-xl overflow-hidden"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
          >
            <div className="bg-gray-900 px-6 pt-6 pb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-900/20 flex-shrink-0">
                  <span className="text-xl font-bold text-white">{getInitials()}</span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-white truncate">{user?.full_name || 'Потребител'}</h2>
                  <p className="text-sm text-white/50 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 leading-none mb-0.5">Пълно име</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{user?.full_name || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 leading-none mb-0.5">Имейл адрес</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{user?.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Physical Data & Generations Card ── */}
          <Card
            className="rounded-xl"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
          >
            <CardHeader className="pb-2 px-6 pt-5">
              <CardTitle className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                Физически данни и генерирания
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Пол', value: profileInfo?.gender ? GENDER_LABELS[profileInfo.gender] || profileInfo.gender : '—' },
                  { label: 'Възраст', value: profileInfo?.age ? `${profileInfo.age} г.` : '—' },
                  { label: 'Ръст', value: profileInfo?.height ? `${profileInfo.height} см` : '—' },
                ].map(f => (
                  <div key={f.label} className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">{f.label}</p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{f.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                {profileInfo?.can_update === false && profileInfo?.next_update_at ? (
                  <span>
                    Физическите данни са заключени · Следваща промяна:{' '}
                    <strong className="text-gray-700 dark:text-gray-300">
                      {new Date(profileInfo.next_update_at).toLocaleDateString('bg-BG', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </strong>
                  </span>
                ) : (
                  <span>Физическите данни се обновяват от калкулатора (веднъж на 30 дни)</span>
                )}
              </div>
              {genStatus && (
                <div
                  data-testid="profile-generation-counter"
                  className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium border ${
                    genStatus.remaining === 0
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700/40 text-red-700 dark:text-red-400'
                      : genStatus.remaining <= 1
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/40 text-amber-800 dark:text-amber-400'
                      : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700/40 text-emerald-800 dark:text-emerald-400'
                  }`}
                >
                  <span>
                    Оставащи генерирания за месеца:{' '}
                    <strong>{genStatus.remaining} от {genStatus.limit}</strong>
                  </span>
                  {genStatus.next_reset && (
                    <span className="text-xs font-normal opacity-70">
                      Нулиране: {new Date(genStatus.next_reset).toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Subscription Card ── */}
          <Card
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 leading-none mb-0.5">Абонамент</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{isActive ? 'Активен' : 'Неактивен'}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isActive
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/50'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-600'
                      }`}>
                        {isActive ? '● Активен' : '○ Неактивен'}
                      </span>
                    </div>
                  </div>
                </div>
                <Link
                  to="/wallet"
                  data-testid="profile-subscription-link"
                  className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                >
                  Управление ›
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* ── Referral Card ── */}
          <Card
            className="rounded-xl"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
          >
            <div className="bg-gradient-to-br from-emerald-700 to-emerald-600 px-6 py-5 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Покани приятел</p>
                  <p className="text-xs text-emerald-200">+1 генерация за теб и за приятеля</p>
                </div>
              </div>
            </div>
            <CardContent className="px-6 py-5 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">Твой реферален линк</p>
                  <p className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-700 rounded-lg px-3 py-2 truncate border border-gray-100 dark:border-slate-600">
                    {referralInfo ? referralInfo.referral_link : 'Зарежда се…'}
                  </p>
                </div>
                <button
                  onClick={handleCopyReferral}
                  disabled={!referralInfo}
                  data-testid="copy-referral-btn"
                  className={`mt-5 w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all ${
                    referralCopied
                      ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600'
                      : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                  }`}
                >
                  {referralCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">Поканени приятели</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{referralInfo?.referrals_count ?? '—'}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center border border-emerald-100 dark:border-emerald-700/30">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Бонус генерации</p>
                  <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">+{referralInfo?.plan_gen_bonus ?? '—'}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
                Сподели линка с приятел. При регистрация и той получава +1 генерация — безплатно.
              </p>
            </CardContent>
          </Card>

          {/* ── Danger Zone ── */}
          <Card
            className="rounded-xl border-red-100 dark:border-red-900/30"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Опасна зона
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-2">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                Изтриването на акаунта е <strong className="text-gray-900 dark:text-gray-100">необратимо</strong>. Всички данни, планове и история на плащанията ще бъдат изтрити завинаги съгласно правото ти на изтриване по GDPR.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-account-button"
                className="px-4 py-2 rounded-xl border border-red-200 dark:border-red-800/50 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700/60 transition-all duration-150"
              >
                Изтрий акаунта
              </button>
            </CardContent>
          </Card>

        </motion.div>
      </div>

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md shadow-2xl"
            data-testid="delete-account-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Изтрий акаунта</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500">Тази операция е необратима</p>
              </div>
            </div>

            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">
              Всички твои данни — планове, профил и платежна история — ще бъдат изтрити завинаги.
            </p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 block">
                  Парола за потвърждение
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder="Въведи паролата си"
                    data-testid="delete-account-password-input"
                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5 block">
                  Напиши <span className="text-red-600 dark:text-red-400 font-bold">ИЗТРИЙ</span> за потвърждение
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="ИЗТРИЙ"
                  data-testid="delete-account-confirm-input"
                  className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Отказ
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || !deletePassword || deleteConfirmText !== 'ИЗТРИЙ'}
                data-testid="delete-account-confirm-button"
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2"
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Изтрива се...
                  </>
                ) : (
                  'Изтрий завинаги'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
